import { useEffect, useRef, useState } from 'react';
import type { Viewer } from '@sfc/sdk';
import { AccountPanel } from '../account/AccountPanel';
import { ApplicationsPanel } from '../applications/ApplicationsPanel';
import { DocumentsPanel } from '../documents/DocumentsPanel';
import { ConversationsPanel } from '../conversations/ConversationsPanel';
import { EnquiriesPanel } from '../enquiries/EnquiriesPanel';
import { InvitationsPanel } from '../invitations/InvitationsPanel';
import { ReportsPanel } from '../reports/ReportsPanel';
import { JournalPanel } from '../journal/JournalPanel';
import { JobsPanel } from '../jobs/JobsPanel';
import { AuditPanel } from '../staff/AuditPanel';
import { StaffPanel } from '../staff/StaffPanel';
import { PlatformStatusPanel } from '../status/PlatformStatusPanel';
import { grouped, visibleSections, type Section } from '../shell/sections';
import { useHashRoute } from '../shell/useHashRoute';
import { Icon } from '../ui/Icon';
import { initials } from '../ui/format';

// Read once when the module loads. A clock that ticks would be motion nobody
// asked for.
const TODAY = new Date().toISOString().slice(0, 10);
const TODAY_LABEL = new Date().toLocaleDateString(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

interface DashboardScreenProps {
  viewer: Viewer;
  onSignOut: () => Promise<void>;
}

/**
 * The dashboard: a sidebar of the sections this viewer may see, a top bar that
 * names the section and says what it is for, and the section itself.
 *
 * Which sections exist comes from the viewer's capabilities, so somebody with a
 * narrower role is not shown a door they will be refused at. The API checks the
 * same rules again on every request; this only decides what is worth showing.
 */
export function DashboardScreen({ viewer, onSignOut }: DashboardScreenProps): React.JSX.Element {
  const sections = visibleSections(viewer.capabilities);
  const { route, navigate } = useHashRoute();
  const active = sections.find((section) => section.id === route) ?? sections[0];

  // Bumped by staff changes so the history reloads alongside them.
  const [staffRevision, setStaffRevision] = useState(0);

  /**
   * Whether the navigation is showing on a narrow screen.
   *
   * On a wide one the sidebar is simply always there and this does nothing:
   * the drawer is a layout the stylesheet adopts under the breakpoint, not a
   * second component. One set of markup means a section cannot be reachable on
   * a laptop and missing on a phone.
   */
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // An empty or unknown address lands on the first section this viewer may
    // see, and the address then says which section that is.
    if (!sections.some((section) => section.id === route) && active) navigate(active.id);
  }, [route, active, sections, navigate]);

  // Arriving somewhere is the end of navigating, so the drawer gets out of the
  // way by itself rather than covering what it was opened to reach.
  useEffect(() => setNavOpen(false), [route]);

  useEffect(() => {
    if (!navOpen) return;

    // Escape closes it, as it closes everything else that covers the page.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    // The page underneath must not scroll while a drawer is over it: a phone
    // that scrolls the wrong layer is the clearest sign something is a web
    // page wearing an app's clothes.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Focus moves into the drawer, so the next Tab is inside what just opened
    // rather than somewhere behind it.
    closeNav.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [navOpen]);

  return (
    <div className={`shell${navOpen ? ' shell--nav-open' : ''}`}>
      {/* Only ever hit on a narrow screen, where the sidebar is a drawer over
          the page. Tapping away from a thing to dismiss it is the gesture
          people already have. */}
      <div
        className="scrim"
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
        data-testid="nav-scrim"
      />

      <aside className="sidebar" id="sidebar-nav">
        <div className="brand">
          <span className="brand__mark-box" aria-hidden="true">
            SFC
          </span>
          <span className="brand__text">
            <span className="brand__mark" data-testid="organization-name">
              {viewer.organization.name}
            </span>
            <span className="brand__sub" data-testid="organization-slug">
              {viewer.organization.slug}
            </span>
          </span>

          {/* Inside the drawer, so it is the first thing focus lands on and
              the way out is where the way in was. */}
          <button
            ref={closeNav}
            className="icon-button icon-button--nav"
            type="button"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            data-testid="close-nav"
          >
            <Icon name="close" />
          </button>
        </div>

        <nav className="nav" aria-label="Sections">
          {grouped(sections).map(({ group, items }) => (
            <div className="nav__group" key={group}>
              <p className="nav__heading">{group}</p>
              {items.map((section) => (
                <NavItem key={section.id} section={section} active={section.id === active?.id} />
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__foot">
          <span className="avatar" aria-hidden="true">
            {initials(viewer.user.displayName)}
          </span>
          <div className="who">
            <span className="who__name">
              <span data-testid="viewer-name">{viewer.user.displayName}</span>
              <span className="pill pill--up" data-testid="viewer-role">
                {viewer.role}
              </span>
            </span>
            <span className="who__meta" data-testid="viewer-email">
              {viewer.user.email}
            </span>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Sign out"
            title="Sign out"
            onClick={() => void onSignOut()}
          >
            <Icon name="signout" />
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button
            className="icon-button icon-button--nav"
            type="button"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            aria-controls="sidebar-nav"
            onClick={() => setNavOpen(true)}
            data-testid="open-nav"
          >
            <Icon name="menu" />
          </button>

          <div className="topbar__id">
            {/* The page's one heading, and every panel labels itself with it,
                so a section is named exactly once for a screen reader. */}
            <h1 className="topbar__title" id="section-heading">
              {active?.label ?? 'Dashboard'}
            </h1>
            {active ? <p className="topbar__description">{active.description}</p> : null}
          </div>
          <div className="topbar__meta">
            <span data-testid="site-name">{viewer.site?.name ?? 'No site'}</span>
            <time dateTime={TODAY}>{TODAY_LABEL}</time>
          </div>
        </header>

        <div className="main__content">
          {/* Keyed by the section so a route change replays exactly one reveal,
              and none of the state of the previous section survives it. */}
          <div className="reveal" key={active?.id}>
            <SectionPanel
              section={active}
              viewer={viewer}
              staffRevision={staffRevision}
              onStaffChanged={() => setStaffRevision((revision) => revision + 1)}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

/** One destination in the sidebar. */
function NavItem({ section, active }: { section: Section; active: boolean }): React.JSX.Element {
  return (
    <a
      href={`#/${section.id}`}
      className={`nav__item${active ? ' nav__item--active' : ''}`}
      aria-current={active ? 'page' : undefined}
      data-testid={`nav-${section.id}`}
    >
      <Icon name={section.id} size={17} />
      <span className="nav__label">{section.label}</span>
    </a>
  );
}

function SectionPanel({
  section,
  viewer,
  staffRevision,
  onStaffChanged,
}: {
  section: Section | undefined;
  viewer: Viewer;
  staffRevision: number;
  onStaffChanged: () => void;
}): React.JSX.Element {
  switch (section?.id) {
    case 'applications':
      return <ApplicationsPanel viewer={viewer} />;
    case 'submissions':
      return <EnquiriesPanel viewer={viewer} />;
    case 'conversations':
      return <ConversationsPanel viewer={viewer} />;
    case 'reports':
      return <ReportsPanel viewer={viewer} />;
    case 'invitations':
      return <InvitationsPanel viewer={viewer} />;
    case 'journal':
      return <JournalPanel viewer={viewer} />;
    case 'jobs':
      return <JobsPanel viewer={viewer} />;
    case 'documents':
      return <DocumentsPanel viewer={viewer} />;
    case 'staff':
      return <StaffPanel viewer={viewer} onChanged={onStaffChanged} />;
    case 'audit':
      return <AuditPanel organizationId={viewer.organization.id} revision={staffRevision} />;
    case 'account':
      return <AccountPanel viewer={viewer} />;
    default:
      return <PlatformStatusPanel />;
  }
}
